import { config } from '../../AuthConfig';
import CredentialsStore from './CredentialsStore';
import { StringMap } from '../../../utils/TypeDefinitions';
import { SentryLogger } from '../../../sentryLogging/SentryLogger';
import { Logger } from '../../Logger';
import { AotCategory, logConstants } from '../../../utils/Constants';
import AuthForm from './AuthForm';
import { storageConstants } from '../../../auth/AuthConstants';

export default class PrePopulatedCredentialsManager {
	private storedCredentials: StringMap = {};
	private credentialsStore: CredentialsStore;

	private constructor(
		readonly authServiceUrl: string,
		readonly referenceUri: string,
		readonly authForm: AuthForm
	) {}

	static async createInstance(
		authServiceUrl: string,
		referenceUri: string,
		authForm: AuthForm
	): Promise<PrePopulatedCredentialsManager> {
		const credentialsManager = new PrePopulatedCredentialsManager(
			authServiceUrl,
			referenceUri,
			authForm
		);
		await credentialsManager.init();
		return credentialsManager;
	}

	private async init(): Promise<void> {
		if (!config.rememberUsername) {
			return;
		}

		this.credentialsStore = await CredentialsStore.createInstance(
			storageConstants.PREPOPULATED_CREDENTIALS_TABLE
		);
		this.storedCredentials = await this.credentialsStore.read(
			this.authServiceUrl,
			this.referenceUri
		);

		this.eraseInvalidStoredCredentials();
	}

	// Done on AML init to ensure credentials are immediately deleted, not just when login form is shown
	static async eraseCredentialsNotAllowedByConfig(): Promise<void> {
		try {
			if (!config.rememberUsername || !config.isUnifiedAuthenticationEnabled) {
				await (
					await CredentialsStore.createInstance(
						storageConstants.PREPOPULATED_CREDENTIALS_TABLE
					)
				).eraseAll();
			}
		} catch (e) {
			SentryLogger.getInstance().logError(e);
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error in PrePopulatedCredentialsManager eraseCredentialsNotAllowedByConfig: ' +
					e.message
			);
		}
	}

	static async eraseCredentialsForStore(referenceUri: string): Promise<void> {
		try {
			const credentialsStore = await CredentialsStore.createInstance(
				storageConstants.PREPOPULATED_CREDENTIALS_TABLE
			);
			await credentialsStore.eraseForStore(referenceUri);
		} catch (e) {
			SentryLogger.getInstance().logError(e);
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error in PrePopulatedCredentialsManager eraseCredentialsForStore: ' + e.message
			);
		}
	}

	/**
	 * Returns saved credentials in encoded format to pre-populate in the UI,
	 * e.g.: "domain=domain&username=userName" or "username=domain%5CuserName" or "username=user%40domain.com"
	 */
	getPrepopulatedCredentials(): string {
		if (!config.rememberUsername || !this.storedCredentials) {
			return '';
		}

		try {
			let prepopulatedCredentials = '';
			if (this.storedCredentials[this.authForm.getUserNameID()]) {
				prepopulatedCredentials += `${encodeURIComponent(
					this.authForm.getUserNameID()
				)}=${this.storedCredentials[this.authForm.getUserNameID()]}`;
			}
			if (this.storedCredentials[this.authForm.getDomainID()]) {
				const delimiter = !!prepopulatedCredentials ? '&' : '';
				prepopulatedCredentials += `${delimiter}${encodeURIComponent(
					this.authForm.getDomainID()
				)}=${this.storedCredentials[this.authForm.getDomainID()]}`;
			}

			return prepopulatedCredentials;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error in PrepopulatedCredentialsManager getPrepopulatedCredentials: ' + e.stack,
				AotCategory.Error,
				'Failed to fetch saved domain and user name from local database'
			);
			return '';
		}
	}

	async onAuthenticationSucceeded(postDataFields: StringMap): Promise<void> {
		if (!config.rememberUsername || !postDataFields) {
			return;
		}

		let updateSavedCredentials = false;
		for (const key of Object.keys(postDataFields)) {
			if (
				this.authForm.isUserNameOrDomainRequirement(key) &&
				(!this.storedCredentials[key] ||
					this.storedCredentials[key] !== postDataFields[key])
			) {
				updateSavedCredentials = true;
				this.storedCredentials[key] = postDataFields[key];
			}
		}

		if (updateSavedCredentials) {
			await this.credentialsStore.save(
				this.authServiceUrl,
				this.referenceUri,
				this.storedCredentials
			);
		}
	}

	// Erase stored credentials when form sent by server does not have a username or domain ID.
	// Erasing stored credentials on changing config is handled in eraseCredentialsNotAllowedByConfig().
	private async eraseInvalidStoredCredentials(): Promise<void> {
		try {
			if (
				!this.authForm.getUserNameID() &&
				!this.authForm.getDomainID() &&
				Object.entries(this.storedCredentials).length !== 0
			) {
				Logger.getInstance().write(
					logConstants.INFO,
					'PrePopulatedCredentialsManager: Erasing stored credentials as form has no username or domain ID.'
				);
				this.storedCredentials = {};
				await this.credentialsStore.erase(this.authServiceUrl, this.referenceUri);
			}
		} catch (e) {
			SentryLogger.getInstance().logError(e);
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error in PrePopulatedCredentialsManager eraseInvalidStoredCredentials: ' +
					e.message
			);
		}
	}
}
